/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * ProblemSection — pain points as accordions.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, Monitor, Brain, Code, LayoutGrid, ChevronDown } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import InfoTooltip from "../InfoTooltip";

const iconMap = { ShieldAlert, Monitor, Brain, Code, LayoutGrid };

export default function ProblemSection({ data }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <PitchSection id="problem" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
        {data.title}
      </h2>
      <p className="text-gray-400 max-w-3xl text-base sm:text-lg mb-12 leading-relaxed">
        {data.lead}
      </p>

      {/* Big stat */}
      {data.stat && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex items-center gap-4 mb-12 p-4 sm:p-6 bg-red-950/20 border border-red-500/20 rounded-2xl max-w-md"
        >
          <span className="text-4xl sm:text-5xl md:text-6xl font-black text-red-400">{data.stat.value}</span>
          <div>
            <p className="text-base text-red-300 font-medium">{data.stat.label}</p>
            <p className="text-xs text-gray-400 mt-1">{data.stat.source}</p>
          </div>
        </motion.div>
      )}

      {/* Pain point accordions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
        {data.painPoints.map((pp, i) => {
          const Icon = iconMap[pp.icon] || LayoutGrid;
          const isOpen = openIndex === i;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className={`bg-[#0F1826]/60 border rounded-2xl transition-all duration-300 ${
                isOpen ? "border-red-500/30 bg-red-950/10" : "border-white/8"
              }`}
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center gap-4 p-5 text-left cursor-pointer"
              >
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-white font-semibold text-base flex items-center flex-1">
                  {pp.title}
                  {pp.tooltip && <InfoTooltip>{pp.tooltip}</InfoTooltip>}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-300 shrink-0 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="text-gray-400 text-sm leading-relaxed px-5 pb-5 pl-14 sm:pl-[76px]">
                      {pp.body}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </PitchSection>
  );
}
